import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import PropTypes from "prop-types";
import Select from "react-select";
import { fetchCondominiumPeriod, fetchOnePeriod } from "../../helpers/api/finance";

const DEFAULT_PAGE_SIZE = 10;

// React (en modo desarrollo, StrictMode) puede invocar un mismo efecto dos
// veces al montar -- si loadPage(1) se dispara dos veces sin cancelarse, las
// dos respuestas terminan aplicadas y options queda con el mismo período
// repetido (dropdown duplicado, y al seleccionarlo, dos chips con el mismo
// id). Dedupear acá por id es la forma más simple y robusta de que options
// nunca pueda tener duplicados, sea cual sea la causa de la doble carga.
const dedupeById = (list) => {
  const seen = new Set();
  return list.filter((p) => {
    const key = String(p.id);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};


const PeriodSelect = forwardRef(
  (
    {
      condominiumId,
      value,
      onChange,
      error,
      placeholder = "Selecciona un período",
      pageSize = DEFAULT_PAGE_SIZE,
      isClearable = true,
      disableGeneratedPlanilla = false,
      inputId,
      isMulti = false,
      availablePeriods = null,
      isLoading = false,
    },
    ref
  ) => {
    const [options, setOptions] = useState([]);
    const [meta, setMeta] = useState({ currentPage: 1, lastPage: 1 });
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");

    // Normaliza `value` a un array de ids seleccionados, sea single o multi,
    // para que el resto del componente no tenga que bifurcar por modo.
    const selectedIds = isMulti
      ? (Array.isArray(value) ? value : []).map(String)
      : value
        ? [String(value)]
        : [];
    const selectedIdsRef = useRef(selectedIds);
    selectedIdsRef.current = selectedIds;

    const loadPage = async (page, searchTerm = "", { append = false } = {}) => {
      if (Array.isArray(availablePeriods)) return availablePeriods;
      if (!condominiumId) return [];
      setLoading(true);
      try {
        const response = await fetchCondominiumPeriod(page, pageSize, {
          condominiumId,
          search: searchTerm,
        });
        const items = response?.data || [];
        setOptions((prev) => {
          if (append) return dedupeById([...prev, ...items]);
          // Un reemplazo de página 1 (ej. al re-disparar el efecto de
          // condominiumId) no debe tirar los períodos ya seleccionados si
          // `prev` los tenía cargados (por el merge puntual de abajo) y la
          // nueva página no los trae -- si no, react-select no encuentra su
          // label por un instante y el campo se ve vacío hasta que el otro
          // efecto lo vuelve a buscar y mezclar.
          const missing = selectedIdsRef.current.filter(
            (id) => !items.some((p) => String(p.id) === id)
          );
          if (missing.length === 0) return dedupeById(items);
          const stillSelected = prev.filter((p) => missing.includes(String(p.id)));
          return dedupeById(stillSelected.length ? [...stillSelected, ...items] : items);
        });
        setMeta({
          currentPage: Number(response?.meta?.current_page || page),
          lastPage: Number(response?.meta?.last_page || page),
        });
        return items;
      } catch (err) {
        console.error("Error cargando períodos", err);
        return [];
      } finally {
        setLoading(false);
      }
    };

    useEffect(() => {
      if (Array.isArray(availablePeriods)) return;
      // eslint-disable-next-line no-console
      console.log("[PeriodSelect DEBUG] condominiumId:", condominiumId, typeof condominiumId);
      if (condominiumId) loadPage(1, "");
      // eslint-disable-next-line
    }, [condominiumId, availablePeriods]);

    // Si algún período seleccionado (ej. al editar, o el que acaba de quedar guardado en el
    // form) no está entre lo ya cargado -- porque cayó en otra página, o porque search lo dejó
    // afuera -- react-select no encuentra su label y el input se muestra vacío, aunque el valor
    // real sigue intacto. Los busca puntual por id y los mezcla en la lista para que se sigan
    // viendo.
    useEffect(() => {
      if (Array.isArray(availablePeriods)) return;
      if (selectedIds.length === 0 || !condominiumId) return;
      const missing = selectedIds.filter(
        (id) => !options.some((p) => String(p.id) === id)
      );
      if (missing.length === 0) return;

      let isMounted = true;
      Promise.all(missing.map((id) => fetchOnePeriod(id, condominiumId).catch(() => null)))
        .then((responses) => {
          if (!isMounted) return;
          const found = responses.map((r) => r?.data).filter(Boolean);
          if (found.length === 0) return;
          setOptions((prev) => {
            const toAdd = found.filter(
              (period) => !prev.some((p) => String(p.id) === String(period.id))
            );
            return toAdd.length ? dedupeById([...toAdd, ...prev]) : prev;
          });
        })
        .catch((err) => console.error("Error cargando el período seleccionado", err));

      return () => {
        isMounted = false;
      };
      // eslint-disable-next-line
    }, [selectedIds.join(","), condominiumId, availablePeriods]);

    useImperativeHandle(ref, () => ({
      reload: (searchTerm = "") => loadPage(1, searchTerm),
    }));

    const handleMenuScrollToBottom = () => {
      if (Array.isArray(availablePeriods)) return;
      if (loading || meta.currentPage >= meta.lastPage) return;
      loadPage(meta.currentPage + 1, search, { append: true });
    };

    const handleInputChange = (inputValue, actionMeta) => {
      if (Array.isArray(availablePeriods)) return;
      if (actionMeta?.action !== "input-change") return;
      setSearch(inputValue);
      if (inputValue.trim() === "") loadPage(1, "");
    };

    const handleKeyDown = (event) => {
      if (Array.isArray(availablePeriods) || event.key !== "Enter" || !search.trim()) return;
      event.preventDefault();
      loadPage(1, search.trim());
    };

    const periodOptions = Array.isArray(availablePeriods)
      ? availablePeriods
      : options;
    const selectOptions = periodOptions.map((p) => ({
      value: p.id,
      label: `${p.name}${disableGeneratedPlanilla && p.has_planilla_generated ? " (planilla ya generada)" : ""}`,
      isDisabled: disableGeneratedPlanilla && p.has_planilla_generated,
      period: p,
    }));

    const selectedOptions = isMulti
      ? selectOptions.filter((opt) => selectedIds.includes(String(opt.value)))
      : selectOptions.find((opt) => String(opt.value) === String(value)) || null;

    const handleChange = (opt) => {
      if (isMulti) {
        const opts = opt || [];
        onChange(
          opts.map((o) => o.value),
          opts.map((o) => o.period)
        );
        return;
      }
      // Segundo argumento opcional con el período completo (name/start_at/end_at/etc.), para
      // los consumidores que necesitan más que el id (ver Reports.jsx). Los que solo usan el
      // id no se ven afectados: reciben el mismo primer argumento de siempre.
      onChange(opt ? opt.value : "", opt ? opt.period : null);
    };

    return (
      <Select
        inputId={inputId}
        isMulti={isMulti}
        options={selectOptions}
        value={selectedOptions}
        onChange={handleChange}
        onMenuScrollToBottom={handleMenuScrollToBottom}
        onInputChange={handleInputChange}
        onKeyDown={handleKeyDown}
        isLoading={loading || isLoading}
        placeholder={placeholder}
        loadingMessage={() => "Cargando..."}
        noOptionsMessage={() => "Sin períodos"}
        isClearable={isClearable}
        styles={{
          control: (base) => ({
            ...base,
            borderColor: error ? "#e63757" : base.borderColor,
          }),
          // El menu trae z-index 1 de fabrica, asi que cualquier cosa que
          // venga despues en el DOM le pasa por encima: en Reportes el
          // interruptor de bolivares se dibujaba sobre la lista de periodos.
          menu: (base) => ({ ...base, zIndex: 5 }),
        }}
      />
    );
  }
);

PeriodSelect.displayName = "PeriodSelect";

PeriodSelect.propTypes = {
  condominiumId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  // Single (isMulti=false): id escalar. Multi (isMulti=true): array de ids.
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.array,
  ]),
  onChange: PropTypes.func.isRequired,
  error: PropTypes.bool,
  placeholder: PropTypes.string,
  pageSize: PropTypes.number,
  isClearable: PropTypes.bool,
  disableGeneratedPlanilla: PropTypes.bool,
  inputId: PropTypes.string,
  isMulti: PropTypes.bool,
  availablePeriods: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool,
};

export default PeriodSelect;
